import { MatchTier, getMatchTier } from '../../../utils/substringMatch';
import type { AdditionalSearchMatcher, SortFilterItem } from '../Autocomplete/types';

const collator = new Intl.Collator();

/**
 * Filters `items` to those whose searchable string contains `searchText`, then sorts by the
 * shared {@link MatchTier} ladder (exact, starts-with, ends-with, contains) and alphabetically
 * within each tier.
 *
 * Uses per-tier buckets rather than wrapping each item in a match object, avoiding hundreds of
 * thousands of short-lived allocations per keystroke at that scale.
 */
export function sortSearchResults<T>(
  items: SortFilterItem<T>[],
  searchText: string,
  getSearchString: (item: SortFilterItem<T>) => string
): SortFilterItem<T>[] {
  return sortLowerSearchResults(items, searchText, createLowerSearchStringGetter(getSearchString));
}

function sortLowerSearchResults<T>(
  items: SortFilterItem<T>[],
  searchText: string,
  getLowerSearchString: (item: SortFilterItem<T>) => string,
  additionalSearchMatcher?: AdditionalSearchMatcher<T>
): SortFilterItem<T>[] {
  const lowerSearch = searchText.toLowerCase();

  const itemsByTier: SortFilterItem<T>[][] = [];

  for (const item of items) {
    const value = getLowerSearchString(item);
    const matchIndex = value.indexOf(lowerSearch);
    if (matchIndex >= 0) {
      const tier = getMatchTier(value, lowerSearch, matchIndex);
      (itemsByTier[tier] ??= []).push(item);
    } else if (additionalSearchMatcher?.(item.item, lowerSearch)) {
      (itemsByTier[MatchTier.Contains] ??= []).push(item);
    }
  }

  const byValue = (a: SortFilterItem<T>, b: SortFilterItem<T>) =>
    collator.compare(getLowerSearchString(a), getLowerSearchString(b));

  return itemsByTier.flatMap(tierItems => tierItems.toSorted(byValue));
}

function createCachedSearchStringGetter<T>(getSearchString: (item: SortFilterItem<T>) => string) {
  const cache = new WeakMap<SortFilterItem<T>, string>();

  return (item: SortFilterItem<T>): string => {
    const cached = cache.get(item);
    if (cached !== undefined) {
      return cached;
    }

    const searchString = getSearchString(item);
    cache.set(item, searchString);
    return searchString;
  };
}

function createLowerSearchStringGetter<T>(getSearchString: (item: SortFilterItem<T>) => string) {
  return createCachedSearchStringGetter((item: SortFilterItem<T>) => getSearchString(item).toLowerCase());
}

/**
 * Creates a sortFilterOverride function that filters by substring match, then sorts by exact,
 * starts-with, ends-with and contains tiers.
 *
 * If supplied, `getSearchString` is normalized and cached by item. When it is omitted, the
 * pre-computed lowercase label and description are searched.
 */
export function createCustomSortFilter<T>(
  getSearchString?: (item: SortFilterItem<T>) => string,
  additionalSearchMatcher?: AdditionalSearchMatcher<T>
) {
  const effectiveGetLowerSearchString =
    getSearchString != null
      ? createLowerSearchStringGetter(getSearchString)
      : createCachedSearchStringGetter((item: SortFilterItem<T>) =>
          item.lowerDescription ? `${item.lowerLabel} ${item.lowerDescription}` : item.lowerLabel
        );

  return (fullList: SortFilterItem<T>[], searchText: string): SortFilterItem<T>[] => {
    return sortLowerSearchResults(fullList, searchText, effectiveGetLowerSearchString, additionalSearchMatcher);
  };
}
